import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

type SourceText = {
  id: string;
  title: string;
  author?: string | null;
};

type ChunkSet = {
  id: string;
  source_text_id?: string;
  chunk_count?: number;
};

type SessionSummary = {
  id: string;
  name: string;
  created_at: string;
  session_chunk_sets?: string[];
  chunk_set_count?: number;
  authors?: string[];
};

type SessionWithSources = SessionSummary & {
  sourceTextIds: string[];
  totalChunkCount: number;
};

type SourceOption = SourceText & {
  sessionCount: number;
  uniqueChunkSetCount: number;
  totalChunkCount: number;
};

type Props = {
  selectedSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  isLoading?: boolean;
};

function formatDate(iso: string): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) {
    return iso;
  }
  return value.toLocaleString();
}

export default function SessionBrowserPanel({ selectedSessionId, onSelectSession, isLoading = false }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [query, setQuery] = useState('');
  const [authorFilter, setAuthorFilter] = useState('ALL');
  const [sourceOptions, setSourceOptions] = useState<SourceOption[]>([]);
  const [sessionsBySourceId, setSessionsBySourceId] = useState<Record<string, SessionWithSources[]>>({});

  useEffect(() => {
    let cancelled = false;

    async function loadCatalog() {
      setLoading(true);
      setError(null);
      try {
        const [sourceTextsRes, chunkSetsRes, sessionsRes] = await Promise.all([
          fetch('/api/source-texts'),
          fetch('/api/chunk-sets'),
          fetch('/api/visualization-sessions'),
        ]);

        if (!sourceTextsRes.ok || !chunkSetsRes.ok || !sessionsRes.ok) {
          throw new Error('Failed to fetch visualization catalog');
        }

        const [sourceTextsData, chunkSetsData, sessionsData] = await Promise.all([
          sourceTextsRes.json(),
          chunkSetsRes.json(),
          sessionsRes.json(),
        ]);

        const sourceTexts: SourceText[] = Array.isArray(sourceTextsData) ? sourceTextsData : [];
        const chunkSets: ChunkSet[] = Array.isArray(chunkSetsData) ? chunkSetsData : [];
        const sessions: SessionSummary[] = Array.isArray(sessionsData) ? sessionsData : [];

        const chunkSetToSource = new Map<string, string>();
        const chunkSetToChunkCount = new Map<string, number>();
        chunkSets.forEach((chunkSet) => {
          if (chunkSet?.id && chunkSet?.source_text_id) {
            chunkSetToSource.set(chunkSet.id, chunkSet.source_text_id);
            chunkSetToChunkCount.set(chunkSet.id, Number(chunkSet.chunk_count || 0));
          }
        });

        const grouped: Record<string, SessionWithSources[]> = {};
        sessions.forEach((session) => {
          const chunkSetIds = Array.isArray(session.session_chunk_sets) ? session.session_chunk_sets : [];
          const sourceTextIds = Array.from(
            new Set(
              chunkSetIds
                .map((chunkSetId) => chunkSetToSource.get(chunkSetId))
                .filter((id): id is string => typeof id === 'string' && id.length > 0)
            )
          );

          if (sourceTextIds.length === 0) {
            return;
          }

          const uniqueChunkSetIds = Array.from(new Set(chunkSetIds));
          const totalChunkCount = uniqueChunkSetIds.reduce((sum, chunkSetId) => {
            return sum + Number(chunkSetToChunkCount.get(chunkSetId) || 0);
          }, 0);
          const enriched: SessionWithSources = { ...session, sourceTextIds, totalChunkCount };
          sourceTextIds.forEach((sourceTextId) => {
            if (!grouped[sourceTextId]) grouped[sourceTextId] = [];
            grouped[sourceTextId].push(enriched);
          });
        });

        const sourceList = sourceTexts
          .filter((source) => Array.isArray(grouped[source.id]) && grouped[source.id].length > 0)
          .map((source) => {
            const sourceSessions = grouped[source.id];
            const uniqueChunkSetIds = Array.from(
              new Set(
                sourceSessions.flatMap((session) =>
                  Array.isArray(session.session_chunk_sets) ? session.session_chunk_sets : []
                )
              )
            );
            const totalChunkCount = uniqueChunkSetIds.reduce(
              (sum, chunkSetId) => sum + Number(chunkSetToChunkCount.get(chunkSetId) || 0),
              0
            );

            return {
              ...source,
              sessionCount: sourceSessions.length,
              uniqueChunkSetCount: uniqueChunkSetIds.length,
              totalChunkCount,
            };
          })
          .sort((a, b) => a.title.localeCompare(b.title));

        Object.values(grouped).forEach((sessionList) => {
          sessionList.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        });

        if (!cancelled) {
          setSourceOptions(sourceList);
          setSessionsBySourceId(grouped);
          setSelectedSourceId((prev) => {
            if (prev && sourceList.some((source) => source.id === prev)) {
              return prev;
            }
            return sourceList[0]?.id || '';
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[SessionBrowserPanel] Failed to load session catalog:', err);
          setError('Failed to load available sessions.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredSourceOptions = useMemo(() => {
    if (authorFilter === 'ALL') {
      return sourceOptions;
    }
    return sourceOptions.filter((source) => (source.author || 'Unknown') === authorFilter);
  }, [authorFilter, sourceOptions]);

  const sessionsForSelectedSource = useMemo(() => {
    if (!selectedSourceId) return [];
    const q = query.trim().toLowerCase();
    return (sessionsBySourceId[selectedSourceId] || []).filter((session) => {
      if (!q) {
        return true;
      }
      const fields = [
        session.name,
        ...session.sourceTextIds,
        ...(session.authors || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return fields.includes(q);
    });
  }, [query, selectedSourceId, sessionsBySourceId]);

  const authorOptions = useMemo(() => {
    const authors = Array.from(
      new Set(
        sourceOptions
          .map((source) => source.author || 'Unknown')
          .filter((author): author is string => typeof author === 'string' && author.length > 0)
      )
    ).sort((a, b) => a.localeCompare(b));
    return ['ALL', ...authors];
  }, [sourceOptions]);

  useEffect(() => {
    setSelectedSourceId((prev) => {
      if (prev && filteredSourceOptions.some((source) => source.id === prev)) {
        return prev;
      }
      return filteredSourceOptions[0]?.id || '';
    });
  }, [filteredSourceOptions]);

  useEffect(() => {
    if (!selectedSessionId) return;
    const sourceIdForSelectedSession = Object.entries(sessionsBySourceId).find(([, sessions]) =>
      sessions.some((session) => session.id === selectedSessionId)
    )?.[0];

    if (
      sourceIdForSelectedSession &&
      sourceIdForSelectedSession !== selectedSourceId &&
      filteredSourceOptions.some((source) => source.id === sourceIdForSelectedSession)
    ) {
      setSelectedSourceId(sourceIdForSelectedSession);
    }
  }, [filteredSourceOptions, selectedSessionId, selectedSourceId, sessionsBySourceId]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading sessions...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Typography variant="body2" color="error">{error}</Typography>;
  }

  if (sourceOptions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No source texts have saved visualization sessions yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search sessions"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Select
        fullWidth
        size="small"
        value={authorFilter}
        onChange={(event) => setAuthorFilter(String(event.target.value || 'ALL'))}
      >
        {authorOptions.map((author) => (
          <MenuItem key={author} value={author}>
            {author === 'ALL' ? 'All authors' : author}
          </MenuItem>
        ))}
      </Select>

      <Select
        fullWidth
        size="small"
        value={selectedSourceId}
        onChange={(event) => setSelectedSourceId(String(event.target.value || ''))}
      >
        {filteredSourceOptions.map((source) => (
          <MenuItem key={source.id} value={source.id}>
            {source.title} {source.author ? `- ${source.author}` : ''} ({source.sessionCount} sessions • {source.uniqueChunkSetCount} chunk sets • {source.totalChunkCount} chunks)
          </MenuItem>
        ))}
      </Select>

      <Box sx={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {sessionsForSelectedSource.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No sessions match the current filters.
          </Typography>
        )}
        {sessionsForSelectedSource.map((session) => {
          const isSelected = selectedSessionId === session.id;
          return (
            <Button
              key={session.id}
              variant={isSelected ? 'contained' : 'outlined'}
              color={isSelected ? 'primary' : 'inherit'}
              onClick={() => onSelectSession(session.id)}
              disabled={isLoading}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                textTransform: 'none',
                px: 1,
                py: 0.75,
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {session.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {formatDate(session.created_at)} • {session.chunk_set_count || 0} chunk set(s) • {session.totalChunkCount} chunks
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}
